import React, { useEffect, useState } from 'react';
import { Box, Grid } from '@mui/material';
import { addMonths, endOfMonth, format, startOfMonth } from 'date-fns';
import { useQueryParams } from 'hooks';
import { getCalendarDateFilter } from 'components/filters/dateFilters';
import { useCalendarEvents, useEventsByDate } from 'hooks/calendarHooks';
import MobileCalendarHeader from 'components/CalendarComponents/MobileCalendarHeader';
import MobileMonthGrid from 'components/CalendarComponents/MobileMonthGrid';
import MobileEventsAgenda from 'components/CalendarComponents/MobileEventsAgenda';
import { IProps } from './EventsCalendarView';

const MobileEventsCalendarView: React.FC<IProps> = ({ top, events }) => {
  const [queryParams, setQueryParams] = useQueryParams();
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const calendarEvents = useCalendarEvents(events, currentDate);
  const eventsByDate = useEventsByDate(calendarEvents);

  useEffect(() => {
    const now = new Date();
    const todayInMonth = now.getFullYear() === currentDate.getFullYear() && now.getMonth() === currentDate.getMonth();

    if (!todayInMonth) {
      setSelectedDate(null);
      return;
    }

    const todayKey = format(now, 'yyyy-MM-dd');
    setSelectedDate(eventsByDate.has(todayKey) ? now : null);
  }, [currentDate, eventsByDate]);

  const navigateToMonth = (newDate: Date) => {
    const normalized = new Date(newDate.getFullYear(), newDate.getMonth(), 1);
    setCurrentDate(normalized);

    setQueryParams({
      ...queryParams,
      ...getCalendarDateFilter(startOfMonth(normalized), endOfMonth(normalized)),
    });
  };

  const handlePrevMonth = () => navigateToMonth(addMonths(currentDate, -1));
  const handleNextMonth = () => navigateToMonth(addMonths(currentDate, 1));
  const handlePrevYear = () => navigateToMonth(addMonths(currentDate, -12));
  const handleNextYear = () => navigateToMonth(addMonths(currentDate, 12));

  const handleDayClick = (date: Date) => {
    setSelectedDate(date);
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        {top}
      </Grid>

      <Grid item xs={12}>
        <Box px={1}>
          <MobileCalendarHeader
            currentDate={currentDate}
            onPrevMonth={handlePrevMonth}
            onNextMonth={handleNextMonth}
            onPrevYear={handlePrevYear}
            onNextYear={handleNextYear}
          />
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Box px={1}>
          <MobileMonthGrid
            currentDate={currentDate}
            eventsByDate={eventsByDate}
            selectedDate={selectedDate}
            onDayClick={handleDayClick}
          />
        </Box>
      </Grid>

      <Grid item xs={12}>
        <MobileEventsAgenda eventsByDate={eventsByDate} sourceEvents={events} selectedDate={selectedDate} />
      </Grid>
    </Grid>
  );
};

export default MobileEventsCalendarView;
